import { css } from "@emotion/css";
import { useState } from "react";
import type { FormEvent } from "react";
import { FiCreditCard, FiAlertCircle } from "react-icons/fi";
import { Button } from "../../../../shared/ui/button/button";
import { Input } from "../../../../shared/ui/input/input";
import { Label } from "../../../../shared/ui/label/label";
import { Select } from "../../../../shared/ui/select/select";
import {
  generateCardToken,
  getPaymentMethodIdFromCardNumber,
  getIssuerIdFromCardNumber,
} from "../../../checkout/utils/payment.utils";

const form = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-md)",
});

const formRow = css({
  display: "flex",
  gap: "var(--spacing-md)",
  "@media (max-width: 520px)": {
    flexDirection: "column",
  },
});

const formGroup = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-2xs)",
  flex: 1,
});

const inputIconWrapper = css({
  position: "relative",
  width: "100%",
});

const inputIcon = css({
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--g-500)",
  zIndex: 1,
  pointerEvents: "none",
});

const inputWithIcon = css({
  paddingLeft: "40px",
});

const errorAlert = css({
  display: "flex",
  alignItems: "flex-start",
  gap: "var(--spacing-sm)",
  padding: "var(--spacing-sm) var(--spacing-md)",
  borderRadius: "var(--spacing-xs)",
  backgroundColor: "rgba(245, 101, 101, 0.1)",
  border: "1px solid rgba(245, 101, 101, 0.3)",
  marginBottom: "var(--spacing-md)",
});

const errorIcon = css({
  color: "rgb(155, 44, 44)",
  flexShrink: 0,
  marginTop: "2px",
});

const errorText = css({
  color: "rgb(155, 44, 44)",
  fontSize: "var(--fs-350)",
  margin: 0,
});

const actions = css({
  display: "flex",
  justifyContent: "flex-end",
  gap: "var(--spacing-sm)",
  marginTop: "var(--spacing-lg)",
  "@media (max-width: 360px)": {
    flexDirection: "column",
  },
});

const cancelButton = css({
  backgroundColor: "transparent",
  border: "1px solid var(--g-300)",
  color: "var(--g-700)",
  "&:hover": {
    backgroundColor: "var(--g-100)",
    border: "1px solid var(--g-300)",
  },
});

export interface ProfilePaymentCardFormProps {
  onSubmit: (data: {
    token: string;
    paymentMethod: string;
    issuerId?: number;
    last4: string;
  }) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export function ProfilePaymentCardForm({
  onSubmit,
  onCancel,
  isLoading = false,
  error: externalError,
}: ProfilePaymentCardFormProps) {
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expirationMonth, setExpirationMonth] = useState("");
  const [expirationYear, setExpirationYear] = useState("");
  const [securityCode, setSecurityCode] = useState("");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const [internalError, setInternalError] = useState<string | null>(null);

  const error = externalError || internalError;

  // Format card number as user types
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(" ");
    } else {
      return value;
    }
  };

  // Handle card number input
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatCardNumber(e.target.value);
    setCardNumber(formattedValue);
  };

  // Format CPF as user types
  const formatCPF = (value: string) => {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
      .substring(0, 14);
  };

  // Handle CPF input
  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIdentificationNumber(formatCPF(e.target.value));
  };

  // Generate a list of years (current year + 20 years ahead)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let i = 0; i < 21; i++) {
      years.push(currentYear + i);
    }

    return years;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Basic validation
    if (
      !cardholderName ||
      !cardNumber ||
      !expirationMonth ||
      !expirationYear ||
      !securityCode ||
      !identificationNumber
    ) {
      setInternalError("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    setInternalError(null);

    try {
      // Clean inputs
      const cleanCardNumber = cardNumber.replace(/\D/g, "");
      const cleanCvv = securityCode.replace(/\D/g, "");

      // Tokenize with MP SDK
      const token = await generateCardToken({
        cardExpirationMonth: String(expirationMonth).padStart(2, "0"),
        cardExpirationYear: String(expirationYear),
        cardholderName,
        cardNumber: cleanCardNumber,
        securityCode: cleanCvv,
      });

      if (!token?.id) {
        throw new Error("Falha ao tokenizar o cartão");
      }

      // Resolve payment method id (bandeira) e issuerId
      const paymentMethodId = await getPaymentMethodIdFromCardNumber(
        cleanCardNumber,
        "credit_card"
      );
      if (!paymentMethodId) {
        throw new Error("Não foi possível identificar a bandeira do cartão");
      }
      const issuerId = await getIssuerIdFromCardNumber(
        cleanCardNumber,
        "credit_card"
      );
      const last4 = cleanCardNumber.slice(-4);

      await onSubmit({
        token: token.id,
        paymentMethod: paymentMethodId,
        issuerId: issuerId ?? undefined,
        last4,
      });
    } catch (err) {
      setInternalError(
        err instanceof Error
          ? err.message
          : "Erro ao processar o cartão. Verifique os dados e tente novamente."
      );
    }
  };

  return (
    <form className={form} onSubmit={handleSubmit}>
      {error && (
        <div className={errorAlert}>
          <FiAlertCircle className={errorIcon} size={18} />
          <p className={errorText}>{error}</p>
        </div>
      )}

      <div className={formGroup}>
        <Label htmlFor="cardholderName">Nome no Cartão</Label>
        <Input
          type="text"
          id="cardholderName"
          placeholder="Como está impresso no cartão"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className={formGroup}>
        <Label htmlFor="cardNumber">Número do Cartão</Label>
        <div className={inputIconWrapper}>
          <FiCreditCard className={inputIcon} size={18} />
          <Input
            type="text"
            id="cardNumber"
            className={inputWithIcon}
            placeholder="0000 0000 0000 0000"
            value={cardNumber}
            onChange={handleCardNumberChange}
            maxLength={19}
            required
            disabled={isLoading}
          />
        </div>
      </div>

      <div className={formRow}>
        <div className={formGroup}>
          <Label htmlFor="expirationMonth">Mês</Label>
          <Select
            id="expirationMonth"
            value={expirationMonth}
            onChange={(e) => setExpirationMonth(e.target.value)}
            required
            disabled={isLoading}
          >
            <option value="" disabled>
              MM
            </option>
            {Array.from({ length: 12 }, (_, i) => {
              const month = String(i + 1).padStart(2, "0");
              return (
                <option key={month} value={month}>
                  {month}
                </option>
              );
            })}
          </Select>
        </div>

        <div className={formGroup}>
          <Label htmlFor="expirationYear">Ano</Label>
          <Select
            id="expirationYear"
            value={expirationYear}
            onChange={(e) => setExpirationYear(e.target.value)}
            required
            disabled={isLoading}
          >
            <option value="" disabled>
              AAAA
            </option>
            {getYearOptions().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </div>

        <div className={formGroup}>
          <Label htmlFor="securityCode">CVV</Label>
          <Input
            type="text"
            id="securityCode"
            placeholder="CVV"
            value={securityCode}
            onChange={(e) => setSecurityCode(e.target.value.replace(/\D/g, ""))}
            maxLength={4}
            required
            disabled={isLoading}
          />
        </div>
      </div>

      <div className={formGroup}>
        <Label htmlFor="identificationNumber">CPF</Label>
        <Input
          type="text"
          id="identificationNumber"
          placeholder="000.000.000-00"
          value={identificationNumber}
          onChange={handleCPFChange}
          required
          disabled={isLoading}
        />
      </div>

      <div className={actions}>
        <Button
          type="button"
          className={cancelButton}
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Processando..." : "Adicionar Cartão"}
        </Button>
      </div>
    </form>
  );
}

export default ProfilePaymentCardForm;
